import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  EstateInputDataFormat_CreateEstateInputDataFormatInput,
  EstateInputDataFormatCategory,
  EstateInputDataFormatDataType,
  EstateInputDataFormatType,
  EstateInputDataFormatUpdateRule,
} from 'queries';
import * as yup from 'yup';

import Modal from 'common/modal';

import EstateInputDataFormatForm from './estate-input-data-format-form';

const useStyles = makeStyles(() => ({
  container: {
    textAlign: 'left',
  },
  button: {
    marginTop: '20px',
  },
}));

const schema = yup.object().shape({
  name: yup.string().required('Required'),
  type: yup.string<EstateInputDataFormatType>().required(),
  dataType: yup.string<EstateInputDataFormatDataType>().when('type', {
    is: EstateInputDataFormatType.Data,
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
  category: yup.string<EstateInputDataFormatCategory>().required(),
  subcategory: yup.string(),
  updateRule: yup.string<EstateInputDataFormatUpdateRule>().required(),
  isOneOffRequiredInfoFormat: yup.boolean().required(),
  templateFieldKey: yup.string().when(['isOneOffRequiredInfoFormat', 'type'], {
    is: (isOneOffRequiredInfoFormat: boolean, type: EstateInputDataFormatType) =>
      isOneOffRequiredInfoFormat === false && type === EstateInputDataFormatType.Data,
    then: (schema) =>
      schema
        .matches(/^[A-Z][A-Za-z]*[1-9]?$/, {
          excludeEmptyString: true,
          message:
            'Only letters and numbers (no special characters or spaces), must start with a capital letter, any numbers must be at the end.',
        })
        .required('Required'),
    otherwise: (schema) => schema.nullable().optional(),
  }),
  documentTagId: yup.string().when('type', {
    is: EstateInputDataFormatType.Document,
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
  omitEmptyPlaceholder: yup.boolean().required(),
});

type CreateEstateInputDataFormatModalProps = {
  onClose: () => void;
  onSubmit: (data: EstateInputDataFormat_CreateEstateInputDataFormatInput) => void;
  createEstateInputDataFormatExecuting: boolean;
  title: string;
  isOneOffRequiredInfoFormat?: boolean;
};

type CreateEstateInputDataFormatFormValues =
  EstateInputDataFormat_CreateEstateInputDataFormatInput & {
    isOneOffRequiredInfoFormat?: boolean;
  };

const CreateEstateInputDataFormatModal = ({
  onClose,
  onSubmit,
  createEstateInputDataFormatExecuting,
  title,
  isOneOffRequiredInfoFormat = false,
}: CreateEstateInputDataFormatModalProps) => {
  const classes = useStyles();
  const formProviderProps = useForm<CreateEstateInputDataFormatFormValues>({
    // @ts-expect-error - RHF V7 limitation #7895
    resolver: yupResolver(schema),
    defaultValues: {
      isOneOffRequiredInfoFormat,
      name: '',
      category: EstateInputDataFormatCategory.MiscellaneousInformation,
      subcategory: '',
      type: EstateInputDataFormatType.Data,
      updateRule: isOneOffRequiredInfoFormat
        ? EstateInputDataFormatUpdateRule.Task
        : EstateInputDataFormatUpdateRule.Format,
      omitEmptyPlaceholder: false,
    },
  });

  const {
    formState: { isSubmitting },
  } = formProviderProps;

  const handleSubmit = (data: CreateEstateInputDataFormatFormValues) => {
    const values = { ...data };
    delete values.isOneOffRequiredInfoFormat;
    onSubmit(values);
  };

  return (
    <Modal
      setShowModal={() => {}}
      onExitClick={onClose}
      showModal={true}
      showActions={false}
      content={
        <FormProvider {...formProviderProps}>
          <form onSubmit={formProviderProps.handleSubmit(handleSubmit)}>
            <Typography variant="h6">{title}</Typography>
            <EstateInputDataFormatForm isOneOffRequiredInfoFormat={isOneOffRequiredInfoFormat} />
            <div className={classes.container}>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                className={classes.button}
                disabled={isSubmitting || createEstateInputDataFormatExecuting}
              >
                Save
              </Button>
            </div>
          </form>
        </FormProvider>
      }
      loading={false}
      actionDisabled={false}
      maxWidth="sm"
    />
  );
};

export default CreateEstateInputDataFormatModal;
